import React, { useEffect } from 'react';
import { Drawer, Form, Input, Select, Button, Divider } from 'antd';
import { CreateI18nConfigInput } from '../graphql/mutations/i18nConfigAdminMutations';
import { POPUP_TYPE_OPTIONS } from '../I18nConfigAdminPage';

const { TextArea } = Input;

/**
 * 新建态包含 messageKey；编辑态提交时会被 handleFinish 剔除（messageKey 创建后不可改，见页面说明）。
 */
export type I18nConfigFormValues = Omit<CreateI18nConfigInput, 'messageKey'> & { messageKey?: string };

type I18nConfigFormDrawerProps = {
  visible: boolean;
  mode: 'create' | 'edit';
  initialValues?: CreateI18nConfigInput & { id?: string };
  onClose: () => void;
  onSubmit: (values: I18nConfigFormValues) => Promise<void> | void;
};

const I18nConfigFormDrawer: React.FC<I18nConfigFormDrawerProps> = ({
  visible,
  mode,
  initialValues,
  onClose,
  onSubmit,
}) => {
  const [form] = Form.useForm<CreateI18nConfigInput>();
  const isEdit = mode === 'edit';

  useEffect(() => {
    if (visible) {
      form.resetFields();
      if (initialValues) {
        form.setFieldsValue(initialValues);
      }
    }
  }, [visible, initialValues, form]);

  const handleFinish = async (raw: CreateI18nConfigInput) => {
    const { messageKey, ...rest } = raw;
    const values: I18nConfigFormValues = isEdit ? rest : raw;
    await onSubmit(values);
  };

  return (
    <Drawer
      open={visible}
      width={640}
      onClose={onClose}
      title={isEdit ? '编辑文案配置' : '新建文案配置'}
      destroyOnClose
      footer={
        <div className="flex justify-end gap-2">
          <Button onClick={onClose}>取消</Button>
          <Button type="primary" onClick={() => form.submit()}>
            保存
          </Button>
        </div>
      }
    >
      <Form layout="vertical" form={form} onFinish={handleFinish}>
        <Form.Item
          label="messageKey"
          name="messageKey"
          rules={[{ required: true, message: '请输入 messageKey' }]}
          extra={isEdit ? '创建后不可改名，避免运行时按旧 key 查找的代码读不到文案' : undefined}
        >
          <Input placeholder="如 TMS_CARD_ROUTE_TYPE_LINEHAUL" disabled={isEdit} />
        </Form.Item>

        <Form.Item
          label="弹窗级别 popupType"
          name="popupType"
          rules={[{ required: true, message: '请选择弹窗级别' }]}
        >
          <Select options={POPUP_TYPE_OPTIONS} placeholder="请选择弹窗级别" />
        </Form.Item>

        <Form.Item label="触发场景（备注用，不参与渲染）" name="triggerScene">
          <Input placeholder="如：TMS任务卡片-路线类型标签-干线" />
        </Form.Item>

        <Divider orientation="left" plain>
          标题（title，可选，六项至少填一项）
        </Divider>
        <Form.Item label="标题-中文" name="titleZh">
          <Input placeholder="可选" />
        </Form.Item>
        <Form.Item label="标题-英文" name="titleEn">
          <Input placeholder="可选" />
        </Form.Item>
        <Form.Item label="标题-西语" name="titleEs">
          <Input placeholder="可选" />
        </Form.Item>

        <Divider orientation="left" plain>
          正文（content，可选，六项至少填一项）
        </Divider>
        <Form.Item label="正文-中文" name="contentZh">
          <TextArea placeholder="可选" autoSize={{ minRows: 2 }} />
        </Form.Item>
        <Form.Item label="正文-英文" name="contentEn">
          <TextArea placeholder="可选" autoSize={{ minRows: 2 }} />
        </Form.Item>
        <Form.Item label="正文-西语" name="contentEs">
          <TextArea placeholder="可选" autoSize={{ minRows: 2 }} />
        </Form.Item>

        <Form.Item label="备注" name="remark">
          <TextArea placeholder="可选" autoSize={{ minRows: 2 }} />
        </Form.Item>
      </Form>
    </Drawer>
  );
};

export default I18nConfigFormDrawer;
